import { getConfiguracion } from './configuracionApiService';
import { mindicadorService } from './mindicadorService';

/**
 * Servicio para manejar tasa de cambio desde configuraciones
 */
class ExchangeRateService {
  constructor() {
    this.cacheKey = 'exchange_rate_cache';
    this.cacheExpiry = 'exchange_rate_cache_expiry';
    this.cacheValidMinutes = 60; // Cache válido por 1 hora
    this.defaultRate = 950;
    this.useMindicador = true; // Usar dólar observado por defecto
  }

  /**
   * Obtiene la tasa de cambio desde cache o API
   */
  async getExchangeRate() {
    try {
      // Verificar cache primero
      const cachedRate = this.getCachedRate();
      if (cachedRate) {
        return cachedRate;
      }

      let rate;
      
      if (this.useMindicador) {
        // Intentar obtener dólar observado primero
        try {
          rate = await mindicadorService.getDolarObservado();
          //console.log('💰 Usando dólar observado:', rate);
        } catch (mindicadorError) {
          console.warn('⚠️ Error obteniendo dólar observado, intentando configuración:', mindicadorError.message);
          // Fallback a configuración de base de datos
          rate = await this.fetchExchangeRateFromApi();
        }
      } else {
        // Usar solo configuración de base de datos
        rate = await this.fetchExchangeRateFromApi();
      }
      
      // Guardar en cache
      this.setCachedRate(rate);
      
      return rate;
    } catch (error) {
      console.error('❌ Error obteniendo tasa de cambio:', error);
      
      // Fallback: usar cache expirado si existe
      const expiredCache = localStorage.getItem(this.cacheKey);
      if (expiredCache) {
        const rate = parseFloat(expiredCache);
        if (!isNaN(rate) && rate > 0) {
          //console.log('📦 Usando tasa de cache expirado:', rate);
          return rate;
        }
      }
      
      // Último fallback: tasa por defecto
      //console.log('🔄 Usando tasa por defecto:', this.defaultRate);
      return this.defaultRate;
    }
  }

  /**
   * Obtiene la tasa desde la API de configuraciones
   */
  async fetchExchangeRateFromApi() {
    try {
      const configuraciones = await getConfiguracion();
      const tasaConfig = configuraciones.find(config => config.clave === 'tasa_cambio_usd_clp');
      
      if (tasaConfig && tasaConfig.valor) {
        const rate = parseFloat(tasaConfig.valor);
        if (!isNaN(rate) && rate > 0) {
          //console.log('💱 Tasa de cambio obtenida desde API:', rate);
          return rate;
        }
      }
      
      throw new Error('Configuración de tasa no encontrada o inválida');
    } catch (error) {
      console.error('❌ Error obteniendo tasa desde API:', error);
      throw error;
    }
  }

  /**
   * Obtiene tasa desde cache si es válida
   */
  getCachedRate() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      const expiry = localStorage.getItem(this.cacheExpiry);
      
      if (cached && expiry) {
        const expiryTime = parseInt(expiry);
        const now = Date.now();
        
        if (now < expiryTime) {
          const rate = parseFloat(cached);
          if (!isNaN(rate) && rate > 0) {
            //console.log('📦 Usando tasa de cambio desde cache:', rate);
            return rate;
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error leyendo cache de tasa:', error);
      return null;
    }
  }

  /**
   * Guarda tasa en cache
   */
  setCachedRate(rate) {
    try {
      const expiry = Date.now() + (this.cacheValidMinutes * 60 * 1000);
      localStorage.setItem(this.cacheKey, rate.toString());
      localStorage.setItem(this.cacheExpiry, expiry.toString());
      //console.log('💾 Tasa de cambio guardada en cache:', rate);
    } catch (error) {
      console.error('❌ Error guardando tasa en cache:', error);
    }
  }

  /**
   * Limpia el cache de tasa de cambio
   */
  clearCache() {
    localStorage.removeItem(this.cacheKey);
    localStorage.removeItem(this.cacheExpiry);
    //console.log('🗑️ Cache de tasa de cambio limpiado');
  }

  /**
   * Convierte USD a CLP usando la tasa actual
   */
  async convertUsdToClp(usdAmount) {
    const rate = await this.getExchangeRate();
    return Math.round(usdAmount * rate);
  }

  /**
   * Obtiene tasa formateada para display
   */
  async getFormattedRate() {
    const rate = await this.getExchangeRate();
    return rate.toLocaleString('es-CL');
  }

  /**
   * Obtiene información completa sobre la tasa actual
   */
  async getExchangeRateInfo() {
    try {
      const rate = await this.getExchangeRate();
      const source = this.useMindicador ? 'Dólar Observado (Banco Central)' : 'Configuración Manual';
      
      return {
        rate,
        formatted: rate.toLocaleString('es-CL'),
        source,
        timestamp: new Date().toISOString(),
        cached: this.getCachedRate() !== null
      };
    } catch (error) {
      console.error('❌ Error obteniendo info de tasa:', error);
      return {
        rate: this.defaultRate,
        formatted: this.defaultRate.toLocaleString('es-CL'),
        source: 'Valor por defecto',
        timestamp: new Date().toISOString(),
        cached: false,
        error: error.message
      };
    }
  }

  /**
   * Fuerza actualización desde mindicador (ignora cache)
   */
  async forceUpdateFromMindicador() {
    try {
      this.clearCache();
      const rate = await mindicadorService.getDolarObservado();
      this.setCachedRate(rate);
      //console.log('🔄 Tasa actualizada forzadamente desde mindicador:', rate);
      return rate;
    } catch (error) {
      console.error('❌ Error forzando actualización:', error);
      throw error;
    }
  }

  /**
   * Configura si usar mindicador o configuración manual
   */
  setUseMindicador(useMindicador) {
    this.useMindicador = useMindicador;
    this.clearCache(); // Limpiar cache al cambiar fuente
    //console.log('⚙️ Fuente de tasa cambiada:', useMindicador ? 'Mindicador' : 'Configuración');
  }
}

// Exportar instancia singleton
export const exchangeRateService = new ExchangeRateService();